import { useMutation, useQueryClient } from '@tanstack/react-query';
import { openTelegramLink } from '@telegram-apps/sdk-react';
import { AnimatePresence, m } from 'motion/react';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, useLive, useMe } from './api';
import { t } from './format';
import { Home } from './screens/Home';
import { Onboarding } from './screens/Onboarding';
import { haptic, markReady } from './telegram';
import { CarHero } from './ui/CarHero';
import { Icon } from './ui/icons';

// Garage paints first; the other screens load in their own chunks (prefetched when idle)
const screens = {
  tasks: () => import('./screens/Tasks'),
  vip: () => import('./screens/Vip'),
  wheel: () => import('./screens/Wheel'),
  rank: () => import('./screens/Rank'),
  profile: () => import('./screens/Profile'),
  draw: () => import('./screens/Draw'),
};
/** A chunk that failed (signal dropped) is fetched again once the connection is back. */
function retrying<T>(load: () => Promise<T>): () => Promise<T> {
  return () =>
    load().catch(
      () =>
        new Promise<T>((resolve, reject) => {
          const again = () => void load().then(resolve, reject);
          if (navigator.onLine) setTimeout(again, 1000);
          else window.addEventListener('online', again, { once: true });
        }),
    );
}
const Tasks = lazy(retrying(() => screens.tasks().then((m) => ({ default: m.Tasks }))));
const Vip = lazy(retrying(() => screens.vip().then((m) => ({ default: m.Vip }))));
const Wheel = lazy(retrying(() => screens.wheel().then((m) => ({ default: m.Wheel }))));
const Rank = lazy(retrying(() => screens.rank().then((m) => ({ default: m.Rank }))));
const Profile = lazy(retrying(() => screens.profile().then((m) => ({ default: m.Profile }))));
const DrawScreen = lazy(retrying(() => screens.draw().then((m) => ({ default: m.DrawScreen }))));

type Tab = 'home' | 'tasks' | 'rank' | 'wheel' | 'vip';
type NavItem = { id: Tab | 'support'; icon: React.ReactNode };
/** Wheel hidden in admin → its slot becomes a Support button that opens the support chat. */
const navItems = (wheel: boolean): NavItem[] => [
  { id: 'home', icon: Icon.garage },
  { id: 'tasks', icon: Icon.tasks },
  { id: 'rank', icon: Icon.rank },
  wheel ? { id: 'wheel', icon: Icon.wheelTab } : { id: 'support', icon: Icon.support },
  { id: 'vip', icon: Icon.vip },
];

function openTg(url: string) {
  if (openTelegramLink.isAvailable()) openTelegramLink(url);
  else window.open(url, '_blank');
}

/** Short notice above the tab bar (Support code copied / Support not connected). */
function Notice({ text, onDone }: { text: string | null; onDone: () => void }) {
  useEffect(() => {
    if (!text) return;
    const id = setTimeout(onDone, 6000);
    return () => clearTimeout(id);
  }, [text, onDone]);
  return (
    <AnimatePresence>
      {text && (
        <m.p
          role="status"
          onClick={onDone}
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 12, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          className="fixed inset-x-4 bottom-[calc(76px+var(--safe-bottom))] z-40 rounded-2xl border border-line-strong bg-raised/95 px-4 py-3 text-sm text-text backdrop-blur"
        >
          {text}
        </m.p>
      )}
    </AnimatePresence>
  );
}

function initialTab(): Tab {
  const screen = new URLSearchParams(location.search).get('screen');
  if (screen === 'rank' || screen === 'vip' || screen === 'wheel') return screen;
  return screen === 'tasks' || screen === 'friends' ? 'tasks' : 'home';
}

function ErrorScreen({ code, onRetry }: { code: string; onRetry: () => void }) {
  const auth = code === 'unauthorized' || code === 'invalid_init_data' || code === 'no_init_data';
  return (
    <div className="grid min-h-dvh place-items-center px-8 text-center">
      <div className="space-y-5">
        <span className="mx-auto block w-fit text-gold-deep">{Icon.ticket}</span>
        <p className="mx-auto max-w-[30ch] text-base leading-relaxed text-text">
          {auth ? t('app.errors.auth') : t('app.errors.network')}
        </p>
        {!auth && (
          <button
            onClick={onRetry}
            className="h-11 rounded-2xl border border-line-strong px-5 text-sm active:scale-[0.98]"
          >
            {t('app.errors.retry')}
          </button>
        )}
      </div>
    </div>
  );
}

/** Offline toast (DESIGN §4 error states): the app keeps the last numbers and catches up. */
function OfflineToast() {
  const [offline, setOffline] = useState(() => !navigator.onLine);
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return (
    <AnimatePresence>
      {offline && (
        <m.p
          role="status"
          initial={{ y: -12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -12, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          className="fixed inset-x-4 top-[calc(12px+var(--safe-top))] z-[60] rounded-2xl border border-line-strong bg-raised/95 px-4 py-3 text-sm text-text backdrop-blur"
        >
          {t('app.offline')}
        </m.p>
      )}
    </AnimatePresence>
  );
}

export function App() {
  const me = useMe();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>(initialTab);
  const [profile, setProfile] = useState(false);
  const [drawOpen, setDrawOpen] = useState(
    () => new URLSearchParams(location.search).get('screen') === 'draw',
  );
  const [flipKey, setFlipKey] = useState(0);
  const opened = useRef(false);

  const onAward = useCallback(() => {
    haptic.success();
    setFlipKey((k) => k + 1);
  }, []);
  useLive(onAward);

  // Count today's app open once per launch (streak); refresh numbers if it paid out.
  useEffect(() => {
    if (!me.data || opened.current) return;
    opened.current = true;
    void api<{ awarded: number }>('/api/app/open', { method: 'POST' }).then((r) => {
      if (r.awarded > 0) void qc.invalidateQueries({ queryKey: ['me'] });
    });
  }, [me.data, qc]);

  useEffect(() => {
    if (me.data || me.error) markReady();
  }, [me.data, me.error]);
  useEffect(() => {
    if (!me.data) return;
    // after the first screen settles: fetch the other tabs so switching is instant
    const id = setTimeout(() => {
      if (!navigator.onLine) return;
      Object.values(screens).forEach((load) => void load().catch(() => {}));
    }, 3500);
    return () => clearTimeout(id);
  }, [me.data]);

  const onboard = useMutation({
    mutationFn: () => api('/api/app/onboarded', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
  const [notice, setNotice] = useState<string | null>(null);
  const clearNotice = useCallback(() => setNotice(null), []);
  const support = useMutation({
    mutationFn: () =>
      api<{ supportUrl: string | null; refCode: string }>('/api/app/support', { method: 'POST' }),
    onSuccess: async (r) => {
      if (!r.supportUrl) return setNotice(t('app.vip.supportMissing'));
      // the business chat link can't carry the code: keep it on the clipboard
      await navigator.clipboard?.writeText(`REF-${r.refCode}`).catch(() => {});
      setNotice(t('app.vip.codeCopied', { ref: r.refCode }));
      openTg(r.supportUrl);
    },
    onError: () => setNotice(t('app.errors.network')),
  });
  const closeProfile = useCallback(() => setProfile(false), []);
  const closeDraw = useCallback(() => setDrawOpen(false), []);

  if (me.error) {
    return (
      <ErrorScreen
        code={me.error instanceof ApiError ? me.error.code : 'network'}
        onRetry={() => me.refetch()}
      />
    );
  }
  if (!me.data) {
    // the garage's hero paints while the session loads, in the exact spot Home puts it
    return (
      <div role="status">
        <span className="sr-only">{t('app.loading')}</span>
        <CarHero reveal={null} alt="" />
        <div className="relative z-10 -mt-14 flex justify-center px-4">
          <m.span
            className="h-[3px] w-16 rounded-full bg-gold"
            animate={{ scaleX: [0.2, 1, 0.2], opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>
      </div>
    );
  }
  const data = me.data;
  const wheelOn = data.features?.wheel !== false;
  // a deep link to the wheel while it's hidden lands on the garage
  const current: Tab = tab === 'wheel' && !wheelOn ? 'home' : tab;

  return (
    <>
      <AnimatePresence mode="wait" initial={false}>
        <m.main
          key={current}
          initial={{ opacity: 0, x: current === 'home' ? -8 : 8 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: current === 'home' ? 8 : -8 }}
          transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
        >
          <Suspense fallback={<div className="min-h-dvh" />}>
            {current === 'home' ? (
              <Home
                me={data}
                flipKey={flipKey}
                onProfile={() => setProfile(true)}
                onTasks={() => setTab('tasks')}
                onDraw={() => setDrawOpen(true)}
              />
            ) : current === 'tasks' ? (
              <Tasks onVip={() => setTab('vip')} />
            ) : current === 'vip' ? (
              <Vip />
            ) : current === 'wheel' ? (
              <Wheel />
            ) : (
              <Rank />
            )}
          </Suspense>
        </m.main>
      </AnimatePresence>

      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-ink/90 pb-[var(--safe-bottom)] backdrop-blur-md">
        <div className="mx-auto flex max-w-md" role="tablist">
          {navItems(wheelOn).map((tb) => {
            const on = current === tb.id;
            return (
              <button
                key={tb.id}
                role="tab"
                aria-selected={on}
                onClick={() => {
                  if (!on) haptic.tap();
                  if (tb.id === 'support') {
                    if (!support.isPending) support.mutate();
                  } else setTab(tb.id);
                }}
                className={`relative flex h-16 flex-1 flex-col items-center justify-center gap-1 text-xs transition-colors ${on ? 'text-gold' : 'text-muted'}`}
              >
                {on && (
                  <m.span
                    layoutId="tab-dot"
                    className="absolute top-0 h-[2px] w-8 rounded-full bg-gold"
                    transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                  />
                )}
                {tb.icon}
                {t(`app.tabs.${tb.id}`)}
              </button>
            );
          })}
        </div>
      </nav>

      <Suspense fallback={null}>
        <AnimatePresence>{profile && <Profile me={data} onClose={closeProfile} />}</AnimatePresence>
        <AnimatePresence>{drawOpen && <DrawScreen onClose={closeDraw} />}</AnimatePresence>
      </Suspense>
      <OfflineToast />
      <Notice text={notice} onDone={clearNotice} />
      {!data.user.onboarded && (
        <Onboarding
          vipReward={data.season.vipReward}
          vipX={data.season.vipX}
          wheel={data.features?.wheel !== false}
          onDone={() => onboard.mutate()}
        />
      )}
    </>
  );
}
